<template>
  <ValidationObserver ref="basicDetails">
    <div class="booking-basic-details">
      <div class="container col-lg-10 col-sm-12 d-flex flex-column">
        <h1 class="pb-3 mt-4">Basic Details</h1>
        <JobItem
          showSalaryRange
          :template="initialTemplate"
          :title="basicDetails.title"
          class="job-item--tile"
        />
        <v-skeleton-loader v-if="isFetchingData" type="article" />
        <div v-else>
          <Permissioned section="bookings.edit" class="d-flex flex-column">
            <ValidationProvider
              v-slot="{ errors }"
              name="roleTitle"
              rules="required|max:250"
            >
              <div class="detail mt-2" permission="role-title">
                <v-text-field
                  label="Role Title"
                  v-model="basicDetails.title"
                  :errorMessages="getErrorMessages(errors)"
                  @change="handleChange"
                />
              </div>
            </ValidationProvider>
            <div class="detail mt-2" permission="deadline-date">
              <DatePicker
                :showValidationErrors="showValidationErrors"
                label="Closing Date"
                validateName="deadlineDate"
                :input-value="basicDetails.deadlineDate"
                @onChange="val => updateData('deadlineDate', val)"
                :minDate="tomorrow"
                required
                isDark
              />
            </div>
            <div class="detail mt-2" permission="start-date">
              <DatePicker
                :showValidationErrors="showValidationErrors"
                @onChange="val => updateData('startDate', val)"
                label="Proposed Start Date"
                validateName="startDate"
                :key="startDatePickerId"
                :input-value="basicDetails.startDate"
                required
                :readonly="!basicDetails.deadlineDate"
                :minDate="basicDetails.deadlineDate"
                isDark
              />
            </div>
          </Permissioned>
        </div>
        <Permissioned section="bookings.edit" vertical>
          <div class="row row--form">
            <ValidationProvider
              v-slot="{ errors }"
              class="col-lg-6 col-md-6"
              name="contractType"
              rules="required"
            >
              <Select
                :errorMessages="getErrorMessages(errors)"
                autocomplete
                v-model="basicDetails.contractType"
                @input="handleChange"
                height="50"
                class="mt-0"
                label="Contract Type"
                placeholder="Select Contract Type"
                :items="contractTypes"
                :loading="isFetchingContractTypes"
                item-text="originalName"
                item-value="originalName"
              />
            </ValidationProvider>
            <ValidationProvider
              v-slot="{ errors }"
              class="col-lg-6 col-md-6"
              name="workingHours"
              rules="required"
            >
              <Select
                :errorMessages="getErrorMessages(errors)"
                autocomplete
                v-model="basicDetails.workingHours"
                @input="handleChange"
                height="50"
                class="mt-0"
                label="Working Hours"
                placeholder="Select Working Hours"
                item-text="name"
                item-value="name"
                :items="workingHours"
                :loading="isFetchingWorkingHours"
              />
            </ValidationProvider>
          </div>
          <div class="detail mt-0" permission="location">
            <ValidationProvider
              v-slot="{ errors }"
              name="location"
              rules="required"
            >
              <Select
                :errorMessages="getErrorMessages(errors)"
                autocomplete
                v-model="basicDetails.location"
                @input="handleChange"
                item-value="value"
                height="50"
                class="mt-0"
                label="Location"
                placeholder="Select location"
                :items="formattedLocations"
                item-text="label"
                :loading="isFetchingData"
              />
            </ValidationProvider>
          </div>
          <div class="detail">
            <ValidationProvider
              v-slot="{ errors }"
              name="grade"
              rules="required"
            >
              <Select
                :errorMessages="getErrorMessages(errors)"
                autocomplete
                v-model="basicDetails.grade"
                @input="handleChange"
                item-text="name"
                item-value="name"
                height="50"
                class="mt-2"
                label="Grade"
                placeholder="Select grade"
                :items="grades"
                :loading="isFetchingGrades"
              />
            </ValidationProvider>
          </div>
          <div class="d-flex align-baseline flex-wrap">
            <v-text-field
              v-money="moneyConfig"
              label="Salary"
              aria-label="Salary Band Lower"
              prefix="£"
              v-model="basicDetails.salaryBandLower"
              @input="handleChange"
            />
            <label class="mx-2">to</label>
            <v-text-field
              aria-label="Salary Band Upper"
              v-money="moneyConfig"
              prefix="£"
              v-model="basicDetails.salaryBandUpper"
              @input="handleChange"
            />
          </div>
          <div class="detail" permission="external-description">
            <div class="mb-2 mt-3">
              External Description
            </div>
            <quill-editor
              v-model="basicDetails.externalDescription"
              @input="handleChange"
              :options="editorOptions"
              class="mb-5"
            />
          </div>
          <div class="detail" permission="internal-description">
            <div class="mb-2 mt-2">Internal Description</div>
            <quill-editor
              v-model="basicDetails.internalDescription"
              @input="handleChange"
              :options="editorOptions"
              class="mb-5"
            />
          </div>
          <div permission="files">
            <div class="detail-title">
              Files
            </div>
            <div class="mt-4">
              <FileUpload
                class="mt-2"
                :uploadingUrl="getAddFileUrl()"
                @unselectFile="unselectFile"
                @selectFile="selectFile"
                :files="basicDetails.files"
                :fileTypes="['application/pdf']"
                disableAutoProceed
                showPreview
                customActions
                isMultiple
                @show="showFile"
              />
            </div>
            <PDFViewFull :data="fileDialog" />
          </div>
        </Permissioned>
      </div>
    </div>
  </ValidationObserver>
</template>

<script>
import JobItem from "@/views/bookings/NewBooking/steps/components/JobItem";
import { DATA_NAMESPACE } from "@/store/modules/data";
import { createNamespacedHelpers } from "vuex";
import APIService from "@/services/APIService";
import {
  FETCH_BOOKING_TEMPLATE,
  FETCH_LOCATIONS,
  FETCH_GRADES,
  FETCH_WORKING_HOURS,
  FETCH_CONTRACT_TYPES
} from "@/store/modules/data/actions";
import {
  GET_BOOKING_TEMPLATE,
  GET_IS_FETCHING_DATA,
  GET_LOCATIONS,
  GET_GRADES,
  GET_IS_FETCHING_GRADES,
  GET_WORKING_HOURS,
  GET_IS_FETCHING_WORKING_HOURS,
  GET_CONTRACT_TYPES,
  GET_IS_FETCHING_CONTRACT_TYPES
} from "@/store/modules/data/getters";
import Select from "@/components/common/Select";
import { getFormattedLocation } from "@/utils/locations";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import DatePicker from "@/components/common/DatePicker";
import Permissioned from "@/components/common/Permissioned";
import { size, remove, forEach } from "lodash";
import { DEFAULT_TOOLBAR_OPTIONS } from "@/plugins/v-quill-editor";
import generateRandomId from "uuid/v4";
import PDFViewFull from "@/components/common/PDFViewFull";
import { getMonetaryDisplayFormat } from "@/utils/helpers";
import FileUpload from "@/components/common/FileUpload";
import { getFilePreviewSettings } from "@/utils/files";
import { DATE_FORMAT_WITH_SEPARATOR_REVERSED } from "@/constants/common";
import moment from "moment";

const { mapActions, mapGetters } = createNamespacedHelpers(DATA_NAMESPACE);

export default {
  name: "BasicDetails",
  props: {
    validate: Boolean,
    initialTemplate: Object
  },
  components: {
    Select,
    JobItem,
    ValidationProvider,
    ValidationObserver,
    DatePicker,
    Permissioned,
    PDFViewFull,
    FileUpload
  },
  data() {
    return {
      basicDetails: {
        title: this.initialTemplate.jobTitle,
        internalDescription: this.initialTemplate.internalDescription,
        externalDescription: this.initialTemplate.externalDescription,
        grade: this.initialTemplate.grade,
        files: [...this.initialTemplate.files],
        contractType: this.initialTemplate.contractType,
        workingHours: this.initialTemplate.workingHours,
        salaryBandLower: getMonetaryDisplayFormat(
          this.initialTemplate.salaryBandLower
        ),
        salaryBandUpper: getMonetaryDisplayFormat(
          this.initialTemplate.salaryBandUpper
        ),
        location: null,
        startDate: null,
        deadlineDate: null
      },
      selectedTemplate: 0,
      showValidationErrors: false,
      editorOptions: {
        modules: {
          toolbar: {
            container: DEFAULT_TOOLBAR_OPTIONS
          }
        }
      },
      startDatePickerId: generateRandomId(),
      fileDialog: { dialog: false },
      uppyInstance: null,
      moneyConfig: { precision: 2 }
    };
  },
  computed: {
    ...mapGetters({
      template: GET_BOOKING_TEMPLATE,
      isFetchingData: GET_IS_FETCHING_DATA,
      locations: GET_LOCATIONS,
      grades: GET_GRADES,
      isFetchingGrades: GET_IS_FETCHING_GRADES,
      workingHours: GET_WORKING_HOURS,
      isFetchingWorkingHours: GET_IS_FETCHING_WORKING_HOURS,
      contractTypes: GET_CONTRACT_TYPES,
      isFetchingContractTypes: GET_IS_FETCHING_CONTRACT_TYPES
    }),
    formattedLocations() {
      return this.locations.map(location => ({
        label: getFormattedLocation(location),
        value: location
      }));
    },
    hasAttachments() {
      return size(this.basicDetails.files);
    },
    tomorrow() {
      return moment()
        .add("days", 1)
        .format(DATE_FORMAT_WITH_SEPARATOR_REVERSED);
    }
  },
  created() {
    const filter = { "not-primary": true };
    this.fetchBookingTemplate(this.initialTemplate.id);
    this.fetchLocations({ filter });
    this.fetchGrades();
    this.fetchWorkingHours();
    this.fetchContractTypes();
  },
  methods: {
    ...mapActions({
      fetchBookingTemplate: FETCH_BOOKING_TEMPLATE,
      fetchLocations: FETCH_LOCATIONS,
      fetchGrades: FETCH_GRADES,
      fetchWorkingHours: FETCH_WORKING_HOURS,
      fetchContractTypes: FETCH_CONTRACT_TYPES
    }),
    async uploadFiles(bookingId) {
      forEach(this.basicDetails.files, ({ id }) =>
        this.setFileUrl(bookingId, id)
      );
      return this.uppyInstance && this.uppyInstance.upload(bookingId);
    },
    async setFileUrl(bookingId, fileId) {
      return this.uppyInstance.setFileState(fileId, {
        xhrUpload: { endpoint: this.getAddFileUrl(bookingId) }
      });
    },
    getAddFileUrl(bookingId) {
      const host = APIService.host;
      return `${host}/bookings/${bookingId}/file`;
    },
    updateData(key, val) {
      if (key === "deadlineDate") {
        this.basicDetails.startDate = null;
        // refresh date picker
        this.startDatePickerId = generateRandomId();
      }
      this.basicDetails[key] = val;
      this.handleChange();
    },
    getErrorMessages(errors) {
      return this.showValidationErrors ? errors : "";
    },
    selectFile({ uppyInstance, file }) {
      this.uppyInstance = uppyInstance;
      this.basicDetails.files.push(file);
    },
    unselectFile({ uppyInstance, file: fileToBeRemoved }) {
      remove(this.basicDetails.files, file => file.id === fileToBeRemoved.id);
      uppyInstance.reset();
    },
    handleChange() {
      this.$emit("change", this.basicDetails);
    },
    async validateForm() {
      this.showValidationErrors = true;
      const isValid = await this.$refs.basicDetails.validate();
      this.$emit(isValid ? "valid" : "invalid");
    },
    size,
    async showFile(file) {
      const filePreviewsettings = await getFilePreviewSettings(file);
      this.fileDialog = {
        ...filePreviewsettings,
        dialog: true
      };
    }
  },
  watch: {
    validate(shouldValidate) {
      if (shouldValidate) {
        this.validateForm();
      }
    },
    initialTemplate(newValue, prevValue) {
      if (newValue.id !== prevValue.id) {
        this.fetchBookingTemplate(this.initialTemplate.id);
      }
    }
  }
};
</script>
<style lang="scss">
.row--form span {
  padding: 10px 15px 0px 15px;
}
</style>
